import { useRef } from 'react';
import { Group, Text, Box } from '@mantine/core';
import { Dropzone, DropzoneProps } from '@mantine/dropzone';
import AfaButton from '../AfaButton/AfaButton';
import AfaFileList, { type AfaFileItem } from '../AfaFileList/AfaFileList';
import { IconUpload } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';

export interface AfaDropzoneProps extends Omit<DropzoneProps, 'children'> {
  /**
   * Texto principal de la zona de dropzone
   * @default "Arrossega i deixa anar alguns fitxers aquí, o fes clic per seleccionar fitxers"
   */
  mainText?: string;
  /**
   * Texto de ayuda adicional
   */
  helperText?: string;
  /**
   * Texto del botón de subir archivos
   * @default "Pujar fitxers"
   */
  buttonText?: string;
  /**
   * Si se muestra el botón de subir archivos
   * @default true
   */
  showButton?: boolean;
  /**
   * Lista de archivos subidos para mostrar
   */
  uploadedFiles?: AfaFileItem[];
  /**
   * Callback cuando se elimina un archivo
   */
  onDeleteFile?: (file: AfaFileItem, index: number) => void;
}

const FORBIDDEN_EXTENSIONS = [
  ".zip", ".rar", ".7z", ".tar.gz",
  ".php", ".phtml", ".asp", ".aspx", ".jsp", ".js",
  ".py", ".rb", ".pl",
  ".exe", ".dll", ".bat", ".cmd", ".sh", ".msi", ".jar",
];

// Detecta si el archivo tiene doble extensión peligrosa
const hasDoubleExtension = (filename: string): boolean => {
  const clean = filename.toLowerCase().trim();

  const dangerousFinalExtensions = [
    'exe', 'js', 'php', 'phtml', 'asp', 'aspx',
    'jsp', 'bat', 'cmd', 'sh', 'dll', 'msi', 'jar'
  ];

  const parts = clean.split('.');

  if (parts.length < 3) return false;

  const lastExtension = parts.at(-1);
  const previousExtension = parts.at(-2);

  if (!lastExtension || !previousExtension) return false;

  return dangerousFinalExtensions.includes(lastExtension);
};

// Detecta si una extensión coincide incluso con casos como .tar.gz
const hasForbiddenExtension = (filename: string) => {
  const lower = filename.toLowerCase();
  return FORBIDDEN_EXTENSIONS.some(ext => lower.endsWith(ext));
};

const AfaDropzone = ({
  mainText = "Arrossega i deixa anar alguns fitxers aquí, o fes clic per seleccionar fitxers",
  helperText,
  buttonText = "Pujar fitxers",
  showButton = true,
  uploadedFiles = [],
  onDeleteFile,
  onDrop,
  ...props
}: AfaDropzoneProps) => {
  const openRef = useRef<() => void>(null);

  const handleDrop = (files: File[]) => {
    // Creamos un Set con los nombres de archivos ya subidos
    const existingFileNames = new Set(
      uploadedFiles.map(file => file.name.toLowerCase())
    );

    // Filtramos los archivos peligrosos y duplicados
    const safeFiles = files.filter(file => {
      const name = file.name;

      if (hasForbiddenExtension(name)) {
        notifications.show({
          title: "Error",
          message: `El fitxer "${name}" té una extensió no permesa.`,
          color: "red",
        });
        console.warn("Archivo bloqueado por extensión prohibida:", name);
        return false;
      }

      if (hasDoubleExtension(name)) {
        notifications.show({
          title: "Error",
          message: `El fitxer "${name}" té una extensió no permesa.`,
          color: "red",
        });
        console.warn("Archivo bloqueado por doble extensión:", name);
        return false;
      }

      // Validación de duplicados
      if (existingFileNames.has(name.toLowerCase())) {
        notifications.show({
          title: "Error",
          message: `El fitxer "${name}" ja està pujat.`,
          color: "red",
        });
        console.warn("Archivo bloqueado por duplicado:", name);
        return false;
      }

      return true;
    });

    // Enviamos solo los archivos seguros al callback original
    if (onDrop && safeFiles.length > 0) {
      onDrop(safeFiles);
    }
  };

  return (
    <Dropzone
      openRef={openRef}
      onDrop={handleDrop}
      activateOnClick={false}
      {...props}
    >
      <Group justify="center" gap="md" mih={120} style={{ pointerEvents: 'none', flexDirection: 'column' }}>
        <div style={{ textAlign: 'center' }}>
          <Text size="md" inline>
            {mainText}
          </Text>
          {helperText && (
            <Text size="sm" c="dimmed" inline mt={7}>
              {helperText}
            </Text>
          )}
          {showButton && (
            <Group justify="center" mt="md">
              <AfaButton
                id="btn-pujar-fitxers"
                variant="outline"
                size="sm"
                leftIcon={IconUpload}
                onClick={() => openRef.current?.()}
                style={{ pointerEvents: 'all' }}
              >
                {buttonText}
              </AfaButton>
            </Group>
          )}
        </div>

        {uploadedFiles.length > 0 && (
          <Box style={{ width: '100%', pointerEvents: 'all' }}>
            <Group gap="md" align="flex-start">
              {uploadedFiles.map((file, index) => (
                <Box key={index} style={{ width: 'calc(33.333% - 10.67px)', minWidth: 0 }}>
                  <AfaFileList
                    variant="readonly"
                    files={[file]}
                    {...(onDeleteFile && { 
                      onDelete: (f, i) => onDeleteFile(file, index)
                    })}
                  />
                </Box>
              ))}
            </Group>
          </Box>
        )}
      </Group>
    </Dropzone>
  );
};

export default AfaDropzone;